import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { user } from 'ampersand-app'
import isObject from 'lodash/isObject'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { errorCodes, registrationStates } from '@/modules/base/util/constants'
import lang from '@/lang'
import { makeStyles } from '@mui/styles'
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { UserService } from '@/modules/account/service/userService'
import DigitForm, { STATUS } from '@/modules/account/components/confirm-email/digit-form'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { ModalBig } from '@/modules/base/components/modal/index'
import ThemeInterface from '@/theme-interface'
import IconConfirmEmail from '@assets/images/confirm_email_with_logo.svg?url'
import { SubTitle, Title } from '@/modules/base/components/texts'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { fetchNewsfeedCount, fetchNewsfeedData } from '@/redux/ducks/newsfeed'
import { isMeinBuero } from '@/utils/build-env'
import { useNavigate } from 'react-router'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import RegistrationStepCard from '@/modules/onboardingV2/components/registration-step-card'
import { registrationBackdrop } from '@/modules/onboardingV2/helpers/registration-backdrop'

const { NOT_CONFIRMED, NOT_CONFIRMED_ERROR } = registrationStates

export const CONFIRM_EMAIL_VARIANTS = Object.freeze({
  default: 'default',
  registration: 'registration',
})
const isMB = isMeinBuero()
const REGISTRATION_MODAL_WIDTH = 560

const useStyles = makeStyles((theme: ThemeInterface) => ({
  modalContainer: {
    [theme.devices.mobile]: {
      height: 'unset',
    },
  },
  modalPaper: {
    padding: '60px 0px',
    width: '80vw',
    maxWidth: 900,
    minHeight: 690,
    [theme.devices.mobile]: {
      width: '100vw',
      height: '100vh',
    },
  },
  description: {
    margin: '0 0 10px',
    fontSize: theme.auth.code.description.fontSize,
  },
  deliveryHintBox: {
    backgroundColor: theme.borderColor,
    borderRadius: 4,
    padding: '10px 5px',
    margin: '20px 0px 25px 0px',
    fontSize: theme.fontSizes.smaller,
    color: '#282D3A',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    [theme.devices.mobile]: {
      justifyContent: 'flex-start',
    },
  },
  header: {
    paddingBottom: 30,
  },
  title: (props: { fontSize?: number }) => ({
    fontSize: props.fontSize || undefined,
    marginBottom: 20,
    textAlign: 'center',
    [theme.devices.mobile]: {
      fontSize: 26,
      lineHeight: '32px',
    },
  }),
  subTitle: {
    margin: '25px 0px 20px 0px',
    textAlign: 'center',
  },
  content: {
    textAlign: 'center',
    width: 550,
    [theme.devices.mobile]: {
      width: '100vw',
      padding: '0px 24px',
    },
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 18,
    gap: 16,
  },
  footerLink: {
    cursor: 'pointer',
  },
  resend: {
    color: theme.primary,
  },
  logout: {
    color: theme.red,
    fontSize: theme.fontSizes.smaller,
  },
  icon: {
    width: 162,
    [theme.devices.mobile]: {
      width: 80,
    },
  },
  registrationPaper: {
    width: REGISTRATION_MODAL_WIDTH,
    // ModalBig also sets a min width - it must not push the modal wider than a phone screen
    minWidth: 0,
    maxWidth: 'calc(100vw - 32px)',
    padding: 0,
    borderRadius: theme.borderRadiusExtraExtraLarge,
    // below 768 px (window width) a full screen page without overlay, like the other registration steps
    [theme.breakpoints.down(768)]: {
      width: '100vw',
      maxWidth: '100vw',
      // 100vh reaches behind the Safari bar - the fixed dialog container ends above it
      height: '100%',
      maxHeight: '100%',
      margin: 0,
      borderRadius: 0,
    },
  },
  registrationContent: {
    padding: '0 !important',
  },
  registrationBackdrop: {
    ...registrationBackdrop(theme),
  },
  registrationError: {
    margin: '0 0 16px',
    fontSize: theme.fontSizes.default,
    color: theme.palette.error.main,
    textAlign: 'center',
  },
  registrationDigitSlot: {
    width: 72,
    maxWidth: 72,
    marginRight: 12,
    // full screen: the four fields share the full width
    [theme.breakpoints.down(768)]: {
      flex: '1 1 0',
      width: 'auto',
      maxWidth: 'none',
      marginRight: 12,
    },
    // the digits are set in the heading typeface (Sora), not in the text one the registration inherits;
    // the field itself: radius 12, a 1 px border, the active one in the brand colour.
    // Only here - the default confirm-email modal (soft reminders) keeps the look of DigitForm.
    '& input': {
      fontFamily: theme.brandFontFamilies.heading,
      fontSize: 32,
      color: theme.palette.brand.text.heading,
      fontWeight: theme.brandTypography.weights.medium,
      border: `1px solid ${theme.palette.brand.inputBorder}`,
      borderRadius: theme.borderRadiusExtraLarge,
      boxShadow: 'none',
      '&:focus': {
        borderColor: theme.palette.brand.main,
      },
    },
  },
  resendButton: {
    fontSize: theme.fontSizes.smedium,
    color: theme.palette.brand.main,
  },
  // "Abmelden" / "Abbrechen" in the colour of the lead text
  secondaryButton: {
    fontSize: theme.fontSizes.smedium,
    color: theme.palette.brand.text.secondary,
  },
}))

interface ConfirmEmailProps {
  isModal: boolean;
  showLogout?: boolean;
  open: boolean;
  title: string;
  fontSize?: number;
  gaData?: {
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
  };
  closeModal: () => void;
  onConfirm?: () => void;
  variant?: typeof CONFIRM_EMAIL_VARIANTS[keyof typeof CONFIRM_EMAIL_VARIANTS];
}

const dataQsIdPrefix = 'confirmEmailModal'

const ConfirmEmailModal: React.FC<ConfirmEmailProps> = (props) => {
  const navigate = useNavigate()
  const {
    isModal,
    open,
    title: titleProps,
    fontSize,
    showLogout,
    gaData,
    closeModal,
    onConfirm,
    variant = CONFIRM_EMAIL_VARIANTS.default,
  } = props
  const classes = useStyles({ fontSize })
  const dispatch = useAppDispatch()
  const [confirmationState, setConfirmationState] = useState(NOT_CONFIRMED)

  const { id, email, firstName, lastName } = useAppSelector((state) => state.user.info)
  const { confirmation } = useAppSelector((state) => state.subscription.details)
  const { hasConfirmedEmail } = confirmation || {}

  const handleConfirmClick = (code: string) => {
    UserService.email.confirmByCode(email, code)
      .then(() => {
        if (gaData && isObject(gaData) && !isMB) {
          const {
            eventCategory,
            eventAction,
            eventLabel,
          } = gaData
          GtmService.pushEvent({
            event: GtmEventTypes.GaEventHub,
            eventCategory,
            eventAction,
            eventLabel,
          })
        }
        // DO-13065: the backend marks the "verify your e-mail" notification as read on confirmation —
        // reload feed + badge so the CTA disappears without a page reload. `user.info.hasConfirmedEmail`
        // and `confirmation` are refreshed by the callers' updateSubscriptionDetails(true, true).
        dispatch(fetchNewsfeedData())
        dispatch(fetchNewsfeedCount())
        // workaround, so if in updateSubscriptionDetails an modal is opened, it does not close directly
        if (onConfirm) {
          onConfirm()
        } else {
          return updateSubscriptionDetails(true, true).then(() => (isModal ? closeModal() : navigate('/')))
        }
      })
      .catch(() => setConfirmationState(NOT_CONFIRMED_ERROR))
  }

  /**
   * @param skipIfCodeValid true for the automatic send on open: the API stays silent while the code of
   *   the last mail is still valid, so reopening the modal does not flood the inbox (DO-13064)
   */
  const sendConfirmationMail = (skipIfCodeValid: boolean) => {
    UserService.email
      .resendConfirmation(email, { skipIfCodeValid })
      .then(() => {
        // the automatic send is silent — the modal text already says that a mail is on its way
        if (!skipIfCodeValid) toast.showToast({ message: lang.confirmResendCode })
      })
      .catch((error) => {
        const emailError = error.body?.meta?.email?.[0]
        const message = emailError?.code === errorCodes.IS_CONFIRMED
          ? lang.accountEmailIsConfirmed
          : lang.defaultErrorMessage
        toast.showToast({ message, type: 'error' })
      })
  }

  const handleResendClick = () => sendConfirmationMail(false)

  // DO-13064: website-configurator users never got a confirmation mail on registration, so the modal
  // requests one as soon as it opens. Once per open — the ref survives re-renders and StrictMode.
  const autoSendDone = useRef(false)
  useEffect(() => {
    if (!open) {
      autoSendDone.current = false
      return
    }
    if (autoSendDone.current || hasConfirmedEmail || !id || !email) return
    autoSendDone.current = true
    sendConfirmationMail(true)
  }, [open, hasConfirmedEmail, id, email])

  const onResend = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault()
    handleResendClick()
  }

  const title = confirmationState === NOT_CONFIRMED_ERROR
    ? lang.confirmEmailCodeError
    : (titleProps || '').replace('{{email}}', email).replace('{{userFullName}}', `${firstName || ''} ${lastName || ''}`.trim()) || lang.confirmEmail.title

  const formStatus = confirmationState === NOT_CONFIRMED_ERROR
    ? STATUS.invalid
    : STATUS.default

  const onLogout = () => {
    closeModal()
    user.logout()
  }

  const isRegistration = variant === CONFIRM_EMAIL_VARIANTS.registration

  const registrationContent = (
    <RegistrationStepCard
      step={1}
      title={lang.registration.confirmEmail.title}
      text={lang.registration.confirmEmail.text(email)}
      dataQsId={`${dataQsIdPrefix}-content`}
      footer={(
        <>
          <ButtonSquare
            className={classes.resendButton}
            label={lang.confirmEmail.resendEmail}
            onClick={onResend}
            dataQsId={`${dataQsIdPrefix}-resend`}
            variant={BUTTON_VARIANTS.link}
          />
          {showLogout
            ? <ButtonSquare variant={BUTTON_VARIANTS.link} className={classes.secondaryButton} label={lang.logoutButtonLabel} onClick={onLogout} dataQsId={`${dataQsIdPrefix}-logout`} />
            : <ButtonSquare variant={BUTTON_VARIANTS.link} className={classes.secondaryButton} label={lang.generics.abort} onClick={closeModal} dataQsId={`${dataQsIdPrefix}-abort`} />}
        </>
      )}
    >
      {confirmationState === NOT_CONFIRMED_ERROR && (
        <p className={classes.registrationError} role='alert' data-qs-id={`${dataQsIdPrefix}-error`}>{lang.confirmEmailCodeError}</p>
      )}
      <DigitForm
        status={formStatus}
        onConfirm={handleConfirmClick}
        slotClassName={classes.registrationDigitSlot}
        dataQsId={`${dataQsIdPrefix}-digit-form`}
      />
    </RegistrationStepCard>
  )

  const defaultContent = (
    <div className={classes.container} data-qs-id={`${dataQsIdPrefix}-content`}>
      <div className={classes.content}>
        <div className={classes.header}>
          <SVGIcon svg={IconConfirmEmail} className={classes.icon} />
        </div>
        <Title className={classes.title}><span dangerouslySetInnerHTML={{ __html: title }} /></Title>
        <SubTitle className={classes.subTitle}>{lang.confirmEmail.subTitle}</SubTitle>
        <div className={classes.description} dangerouslySetInnerHTML={{ __html: lang.confirmEmail.description(email) }} />
        <div className={classes.deliveryHintBox}>{lang.confirmEmail.deliveryHint}</div>
        <DigitForm
          status={formStatus}
          onConfirm={handleConfirmClick}
          dataQsId={`${dataQsIdPrefix}-digit-form`}
        />
      </div>
      <footer className={classes.footer}>
        <div
          className={clsx(classes.footerLink, classes.resend)}
          onClick={onResend}
          data-qs-id={`${dataQsIdPrefix}-resend`}
        >
          {lang.confirmEmail.resendEmail}
        </div>
        <div className={clsx(classes.footerLink, showLogout ? classes.logout : '')} onClick={showLogout ? onLogout : closeModal }>
          {showLogout ? lang.logoutButtonLabel : lang.generics.abort}
        </div>
      </footer>
    </div>
  )

  if (hasConfirmedEmail || !id) {
    return null
  }

  const content = isRegistration ? registrationContent : defaultContent

  return isModal
    ? (
      <ModalBig
        dataQsId={`${dataQsIdPrefix}-modal`}
        open={open}
        // ModalBig pins min and max width to this value (600 by default); the registration modals are 560 wide
        width={isRegistration ? REGISTRATION_MODAL_WIDTH : undefined}
        className={isRegistration ? classes.registrationPaper : classes.modalPaper}
        // the registration fills the screen on mobile - the old modal's container would cut its height to the content
        classNameContainer={isRegistration ? undefined : classes.modalContainer}
        dialogContentClass={isRegistration ? classes.registrationContent : undefined}
        slotProps={isRegistration ? { backdrop: { className: classes.registrationBackdrop } } : undefined}
        showCloseIcon={false}
      >
        {content}
      </ModalBig>
    )
    : (
      <>{content}</>
    )
}

export default ConfirmEmailModal
